import React, { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import './DocsPage.css';

const TABS = [
  { id: 'user', label: 'Utilisateur' },
  { id: 'admin', label: 'Établissement' },
  { id: 'tech', label: 'Technique' },
];

const DOCS = {
  user: {
    title: "Guide Utilisateur",
    description: "Tout ce qu'il faut savoir pour utiliser CrousRadar au quotidien.",
    pdf: "/docs/guide-utilisateur.pdf",
    sections: [
      {
        question: "Comment consulter l'affluence d'un restaurant ?",
        answer: "Ouvre l'app, la carte affiche tous les restaurants autour de toi avec un code couleur : vert (vide), orange (moyen), rouge (blindé). Clique sur un restaurant pour voir le détail."
      },
      {
        question: "Comment signaler l'affluence ?",
        answer: "En bas de la fiche restaurant, appuie sur \"Mettre à jour l'affluence\" et choisis le niveau actuel (Vide, Moyen, Blindé). Tu gagnes +50 XP à chaque signalement validé."
      },
      {
        question: "Comment fonctionne le classement ?",
        answer: "Chaque signalement te rapporte de l'XP. Tu montes dans le classement global et celui entre amis. Des badges et trophées sont débloqués à certains paliers."
      },
      {
        question: "Comment ajouter des amis ?",
        answer: "Va dans l'onglet Profil, puis \"Ajouter un ami\" avec leur pseudo. Tu pourras ensuite voir leur position dans le classement Amis."
      },
      {
        question: "Les alertes, comment ça marche ?",
        answer: "Active les notifications pour tes restaurants favoris. Dès que quelqu'un signale un changement d'affluence, tu reçois une alerte en temps réel."
      },
    ]
  },
  admin: {
    title: "Guide Établissement",
    description: "Documentation pour les gestionnaires de restaurants universitaires et partenaires CROUS.",
    pdf: "/docs/guide-etablissement.pdf",
    sections: [
      {
        question: "Comment accéder au tableau de bord ?",
        answer: "Rendez-vous sur crousradar.fr/etablissement/login avec vos identifiants fournis lors de l'activation de votre compte partenaire."
      },
      {
        question: "Quelles données sont disponibles ?",
        answer: "Vous accédez aux tendances d'affluence en temps réel et historique, aux heures de pointe, et aux comparatifs jour par jour pour chacun de vos restaurants."
      },
      {
        question: "Comment demander un accès partenaire ?",
        answer: "Remplissez le formulaire sur crousradar.fr/etablissement/demande-acces-partenaire. Notre équipe vous recontacte sous 48h pour activer votre espace."
      },
      {
        question: "Les données sont-elles fiables ?",
        answer: "Les signalements sont croisés et pondérés algorithmiquement. Plus la communauté est active sur votre campus, plus la donnée est précise."
      },
    ]
  },
  tech: {
    title: "Documentation Technique",
    description: "Architecture, API et intégrations pour les développeurs et équipes techniques.",
    pdf: "/docs/documentation-technique.pdf",
    sections: [
      {
        question: "Quelle stack technique ?",
        answer: "Application mobile React Native (Expo) sur iOS et Android. Frontend web React. Backend Firebase (Realtime Database, Cloud Functions, Auth). Hébergement UE via Firebase europe-west1."
      },
      {
        question: "Y a-t-il une API publique ?",
        answer: "Une API en lecture est en cours de développement pour permettre aux campus d'intégrer les données d'affluence dans leurs propres outils. Contactez-nous pour un accès anticipé."
      },
      {
        question: "Comment sont traitées les données ?",
        answer: "Les signalements sont anonymisés et agrégés. Aucune donnée personnelle n'est revendue. Hébergement souverain en Europe (datacenter Firebase eu-west)."
      },
      {
        question: "Peut-on contribuer au projet ?",
        answer: "CrousRadar n'est pas open source pour le moment, mais nous sommes ouverts aux collaborations techniques avec les DSI d'universités et les équipes CROUS."
      },
    ]
  }
};

function DocsPage() {
  const [activeTab, setActiveTab] = useState('user');
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const doc = DOCS[activeTab];

  return (
    <div className="docs-wrapper">
      <div className="docs-container">
        <header className="docs-header">
          <h1 className="docs-title">Documentation</h1>
          <p className="docs-subtitle">Guides, FAQ et ressources — en accès libre.</p>
        </header>

        <div className="docs-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              className={`docs-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => { setActiveTab(tab.id); setOpenFaq(null); }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="docs-content">
          <div className="docs-intro">
            <div>
              <h2 className="docs-section-title">{doc.title}</h2>
              <p className="docs-section-desc">{doc.description}</p>
            </div>
            <a href={doc.pdf} download className="docs-download-btn">
              <Download size={18} />
              Télécharger le PDF
            </a>
          </div>

          <div className="docs-faq">
            <h3 className="docs-faq-title">Questions fréquentes</h3>
            {doc.sections.map((item, i) => (
              <div
                key={i}
                className={`docs-faq-item ${openFaq === i ? 'open' : ''}`}
              >
                <button
                  className="docs-faq-question"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span>{item.question}</span>
                  <span className="docs-faq-toggle">{openFaq === i ? '−' : '+'}</span>
                </button>
                {openFaq === i && (
                  <div className="docs-faq-answer">
                    <p>{item.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DocsPage;
